import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormikProps } from 'formik';
import {
  withInject,
  Action,
} from '@piwikpro/platform';
import {
  Text,
  Stack,
  Card,
  Spinner,
  withTranslation,
  Layout,
  TFunction,
} from '@piwikpro/ui-components';
import { ContainerSettings } from '../../ContainerSettings';
import FailMessage from '../FailMessage';
import { ContainerSettingsState } from '../../redux';
import { DomainSetting } from '../../redux/appDomainSettings';

const mapStateToProps = (state: ContainerSettingsState) => ({
  isLoading: state.containerSettings.app.secureCookie.isLoading,
  failed: state.containerSettings.app.secureCookie.failed,
  value: state.containerSettings.app.secureCookie.value,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  containerSettings: ContainerSettings
}

export interface Props extends ReduxProps, InjectedProps {
  appId: string
  controlWrapper?: typeof Action
  t: TFunction
}

interface FormValues {
  secureCookieEnabled: boolean
}

export class CookiesSection extends React.Component<Props> {
  private settingName: DomainSetting = 'secure_cookies';

  constructor(props: Props) {
    super(props);

    this.handleSwitcherSubmit = this.handleSwitcherSubmit.bind(this);
  }

  async componentDidMount() {
    const { containerSettings, appId } = this.props;

    try {
      await containerSettings.getAppSetting(appId, this.settingName);
      // eslint-disable-next-line no-empty
    } catch {}
  }

  async handleSwitcherSubmit(
    values: FormValues,
    { setFieldValue, setSubmitting }: FormikProps<FormValues>,
  ) {
    const {
      containerSettings,
      appId,
    } = this.props;

    try {
      await containerSettings.updateAppSetting(appId, this.settingName, {
        value: {
          // eslint-disable-next-line camelcase
          is_enabled: values.secureCookieEnabled,
        },
      });
    } catch {
      setFieldValue('secureCookieEnabled', !values.secureCookieEnabled, false);
    }

    setSubmitting(false);
  }

  render() {
    const {
      value,
      isLoading,
      failed,
      controlWrapper,
      t,
    } = this.props;
    const ActionWrapper = controlWrapper || Action;
    // eslint-disable-next-line camelcase
    const initialValue = value?.is_enabled;

    return (
      <Layout>
        <Layout.AnnotatedSection title={t('apps.details.privacy.cookies.title')} />
        <Layout.Column dominant>
          {isLoading && (
            <Card>
              <Card.Section>
                <Spinner />
              </Card.Section>
            </Card>
          )}
          {failed && <FailMessage />}
          {value && (
            <Card>
              <Card.Section>
                <Stack wrap verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack vertical spacing="narrow">
                      <Stack.Item>
                        <Text weight={700} wrapType="wrap" size={20} as="h2">
                          {t('apps.details.privacy.cookies.use-secure-cookies')}
                        </Text>
                      </Stack.Item>
                      <Stack.Item>
                        <Text wrapType="wrap" as="p">
                          {t('apps.details.privacy.cookies.use-secure-cookies-description')}
                        </Text>
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item>
                    <ActionWrapper.AutoSavedSwitcherControl
                      id="container-settings-secure-cookie-switcher"
                      initialValue={initialValue}
                      name="secureCookieEnabled"
                      onSubmit={this.handleSwitcherSubmit}
                    />
                  </Stack.Item>
                </Stack>
              </Card.Section>
            </Card>
          )}
        </Layout.Column>
      </Layout>
    );
  }
}

export default connector(withInject<InjectedProps>({
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
})(withTranslation(['administration'])(CookiesSection)));
